import Beacon from '../Beacon.jsx';
import { useCompanyBilling } from '../CompanyBilling.jsx';
import { campaignSpendAccess, campaignBudgetAccess, campaignBudgetBaseline } from '../../campaign-billing-controls.js';
import { CampaignBillingAccessDialog, CampaignBillingAccessNotice } from './CampaignBillingAccess.jsx';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  api,
  curatedCampaignManagementApi,
  CURATED_CAMPAIGN_LIST_PAGE_SIZE,
} from '../../api.js';
import { hasNamedTenantAction } from '../../product-policy.js';
import { safeFailureMessage } from '../../safe-failure.js';
import {
  CONTINUITY_SUMMARY_REFETCH_DELAY_MS,
  createContinuityRefetchScheduler,
  summaryBatchIsRefreshing,
} from '../../continuity-refresh.js';
import {
  useConfirmationDialog,
} from '../ConfirmationDialog.jsx';
import {
  CampaignMetricStrip,
  CampaignPerformanceContext,
  campaignPerformanceFreshness,
  campaignPerformanceCompleteness,
  campaignReportingPeriod,
  campaignReportingCurrency,
  CampaignSettings,
  CampaignStatePill,
  campaignStatusTone,
  campaignFullDetailDestination,
  normalizeCampaignRowSummaries,
  normalizeCampaignRowSummaryBatch,
} from './CampaignSummaryCard.jsx';
import { Button, Chip } from '../primitives.jsx';
import Select from '../Select.jsx';
import {
  IconAddition,
  IconArchive,
  IconBasement,
  IconBathroom,
  IconCampaign,
  IconChevronDown,
  IconClose,
  IconExternal,
  IconGrid,
  IconKitchen,
  IconList,
  IconPauseStroke,
  IconPlayStroke,
  IconRestore,
  IconSearch,
  IconSliders,
  IconVideo,
  IconTrash,
} from '../icons.jsx';
import {
  normalizeCampaignManagementResponse,
  campaignManagementRetryKey,
  normalizeCampaignDeletionResponse,
  campaignDeleteBlockerMessage,
  campaignDeleteRetryWaitMinutes,
  useCampaignDeleteRetryWait,
} from './CampaignManagementControls.jsx';
import {
  campaignRowSummaryReadCoordinator,
  campaignRowSummaryReadScopeKey,
} from '../../campaign-row-summary-read-coordinator.js';
import {
  CampaignListPagination,
  CampaignSummaryBatchStatus,
  normalizeCampaignListPageResponse,
  emptyCampaignListPage,
  campaignRowSummaryPath,
  campaignControlsForPolicy,
  canonicalMonthlyMajor,
  scopedOperationRetryKey,
  mutationBody,
  mutationOperationId,
  durableMutationPresentation,
  durableMutationReconciliation,
  validatedCampaignDetail,
  invalidateCampaignManagementList,
  statusLabel,
  campaignDeleteDialogOptions,
  hydrateRetryStateFromDetail,
} from '../../screens/CuratedCampaigns.jsx';
import { CampaignModal, CampaignSettingsDrawer, CampaignConfirmationDialog } from './CampaignOverlays.jsx';
import { CampaignAds } from './CampaignAds.jsx';
import { CampaignInventoryOverview } from './CampaignInventoryOverview.jsx';

const PERIODS = [
  ['today', 'Today'],
  ['last_7d', 'Last 7 days'],
  ['last_30d', 'Last 30 days'],
  ['maximum', 'Lifetime'],
];
const STATUS_FILTERS = [
  ['', 'All campaigns'],
  ['active', 'Active'],
  ['paused', 'Paused'],
  ['draft_review', 'Drafts & review'],
  ['archived', 'Archived'],
];
export function normalizeInventoryStatusCounts(value) {
  const keys = ['all', 'active', 'paused', 'draft_review', 'archived'];
  if (!value || keys.some((key) => !Number.isSafeInteger(value[key]) || value[key] < 0)
    || value.active + value.paused + value.draft_review > value.all) return null;
  return Object.fromEntries(keys.map((key) => [key, value[key]]));
}

export function inventorySpendSortCurrency(campaigns, summaries) {
  const currencies = new Set(campaigns.map(({ id }) => summaries.get(id))
    .filter((summary) => summary?.performance?.metrics?.spend?.availability === 'available')
    .map(campaignReportingCurrency).filter(Boolean));
  return currencies.size === 1 ? [...currencies][0] : '';
}

export function inventoryStatusCountsForCompany(inventory, companyId) {
  return inventory?.companyId === companyId ? inventory.statusCounts : null;
}

export function sortInventoryPage(campaigns, summaries, sort) {
  const key = sort === 'leads' ? 'metaReportedLeads' : sort === 'spend' ? 'spend' : '';
  if (!key || (key === 'spend' && !inventorySpendSortCurrency(campaigns, summaries))) return campaigns;
  const amount = (campaign) => {
    const summary = summaries.get(campaign.id);
    if (key === 'spend' && !campaignReportingCurrency(summary)) return -1;
    const metric = summary?.performance?.metrics?.[key];
    return metric?.availability === 'available' && Number.isFinite(metric.value) ? metric.value : -1;
  };
  // This deliberately orders only the already bounded page, never implies a
  // provider-wide ranking, and does not alter the server's paging cursor.
  return [...campaigns].sort((a, b) => amount(b) - amount(a));
}
const remembered = new Map();
const LIST_INVALIDATED_EVENT = 'beyondleads:campaign-management-list-invalidated';
const TERMINAL_MANAGEMENT_REJECTIONS = new Set([
  'campaign_archive_workflow_in_progress',
  'campaign_archive_publish_in_progress',
  'campaign_archive_provider_control_in_progress',
  'campaign_archive_lead_ingress_in_progress',
  'campaign_archive_form_operation_in_progress',
  'campaign_archive_form_reservation_recovery_required',
  'campaign_revision_stale',
  'campaign_management_idempotency_conflict',
]);
export function retainInventoryManagementRetry(error) {
  return !TERMINAL_MANAGEMENT_REJECTIONS.has(error?.code);
}
export function campaignBudgetMutationCurrency(detail) {
  // The mutation adapter interprets monthlyMajor in the Meta ad account currency.
  // Unavailable Insights can carry a planning-currency placeholder; only a
  // completed provider read proves which currency the monetary confirmation uses.
  const verifiedBudget = campaignBudgetBaseline(detail);
  if (verifiedBudget) return verifiedBudget.currency;
  const currency = detail?.metrics?.account?.currency;
  const freshness = detail?.metrics?.freshness;
  return typeof currency === 'string' && /^[A-Z]{3}$/.test(currency)
    && ['available', 'no_data'].includes(freshness?.state)
    && Number.isFinite(Date.parse(freshness?.retrievedAt || ''))
    ? currency : '';
}
const headers = (companyId) => ({ headers: { 'X-Company-Id': companyId } });
const detailPath = (campaignId, datePreset) =>
  `/api/saas/campaigns/${encodeURIComponent(campaignId)}/operations?datePreset=${datePreset}`;

// OGC-445 ghost icon squares: sizes stay in CSS (36px desktop / 44px phone),
// only the padding and centring ride on the shared Button primitive.
const ICON_BUTTON_STYLE = Object.freeze({ padding: 0, justifyContent: 'center' });
const DETAILS_BUTTON_STYLE = Object.freeze({ padding: 'var(--campaign-details-padding, 8px 10px)' });

// OGC-445: one icon set. CampaignIcon is a thin alias that maps the campaign
// glyph names onto the shared icons.jsx exports (stroke 1.6, aria-hidden).
const CAMPAIGN_GLYPHS = Object.freeze({
  settings: IconSliders,
  pause: IconPauseStroke,
  resume: IconPlayStroke,
  archive: IconArchive,
  restore: IconRestore,
  delete: IconTrash,
  list: IconList,
  cards: IconGrid,
  chevron: IconChevronDown,
  kitchen: IconKitchen,
  bathroom: IconBathroom,
  basement: IconBasement,
  addition: IconAddition,
  video: IconVideo,
  external: IconExternal,
  close: IconClose,
  search: IconSearch,
  campaign: IconCampaign,
});
export function CampaignIcon({ name, size = 18 }) {
  const Glyph = CAMPAIGN_GLYPHS[name] || CAMPAIGN_GLYPHS.campaign;
  return <Glyph size={size} aria-hidden="true" focusable="false" />;
}

export function inventoryPreferences(companyId) {
  if (remembered.has(companyId)) return remembered.get(companyId);
  let view = 'list';
  try {
    if (
      typeof window !== 'undefined' &&
      globalThis.localStorage?.getItem('beyondleads-campaign-view') === 'cards'
    )
      view = 'cards';
  } catch {
    /* Storage can be unavailable in private browsing. */
  }
  return {
    view,
    search: '',
    status: '',
    renovationTypeId: '',
    sort: 'recent',
    datePreset: 'last_30d',
  };
}

export const CampaignQuickPanel = CampaignSettingsDrawer;

export function CampaignInventoryRow({
  campaign,
  summary,
  summaryStatus,
  view,
  datePreset,
  expanded,
  busy,
  onToggle,
  onAction,
  onDetails,
  productPolicy,
  children,
}) {
  const id = useId().replaceAll(':', '');
  const archived = Boolean(campaign.archivedAt);
  const currentStatus = summary?.settings?.status || campaign.status;
  const budget = summary?.settings?.monthlyBudget;
  const types = summary?.settings?.renovationTypes || [];
  const typeSymbol = ['kitchen', 'bathroom', 'basement', 'addition'].includes(types[0]?.toLowerCase())
    ? types[0].toLowerCase() : 'campaign';
  const lifecycle =
    currentStatus === 'active'
      ? 'pause'
      : currentStatus === 'paused'
        ? 'resume'
        : '';
  const budgetLabel = budget?.monthlyMajor
    ? new Intl.NumberFormat(undefined, {
        ...(budget.currency
          ? { style: 'currency', currency: budget.currency }
          : {}),
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(Number(budget.monthlyMajor))
    : '—';
  const freshness = campaignPerformanceFreshness(summary, summaryStatus);
  const completeness = campaignPerformanceCompleteness(summary);
  // OGC-490: a campaign Meta will no longer confirm the original login for
  // shows no metrics and cannot be paused or resumed here, so the row says so
  // instead of implying historical performance; Details explains what to do.
  const republishNeeded = !archived && summary?.metaAccess?.state === 'republish_needed';
  const lifecycleBlocker = republishNeeded && lifecycle
    ? `Can't ${lifecycle} here: Meta can no longer confirm the login this campaign was published with. Open Details.`
    : '';
  const refreshingAccess = !archived && summary?.metaAccess?.state === 'refreshing';
  const rowNote = republishNeeded ? "Can't be managed here · open Details"
    : refreshingAccess ? 'Refreshing Meta access'
      : completeness?.label || (currentStatus === 'paused' ? 'Campaign paused · historical performance'
        : ['draft', 'awaiting_approval', 'approved'].includes(currentStatus) ? 'Performance appears after delivery' : freshness.label);
  const actions = (
        <div
          className="bl-campaign-summary-actions"
          aria-label={`Quick actions for ${campaign.name}`}
        >
          <Button
            variant="ghost"
            size="sm"
            type="button"
            className="bl-campaign-icon-button"
            style={ICON_BUTTON_STYLE}
            title="Ad settings"
            aria-label={`Ad settings for ${campaign.name}`}
            onClick={() => onAction('settings')}
            disabled={busy}
          >
            <CampaignIcon name="settings" />
          </Button>
          {!archived && (
            <Button
              variant="ghost"
              size="sm"
              type="button"
              className="bl-campaign-icon-button"
              style={ICON_BUTTON_STYLE}
              title={
                lifecycleBlocker
                  ? lifecycleBlocker
                  : lifecycle
                    ? `${lifecycle === 'resume' ? 'Resume' : 'Pause'} campaign`
                    : 'Open Details for setup and delivery controls'
              }
              aria-label={`${lifecycle === 'resume' ? 'Resume' : 'Pause'} campaign ${campaign.name}`}
              // OGC-490: the title is not announced; screen readers get the reason here.
              aria-describedby={lifecycleBlocker ? `campaign-lifecycle-blocker-${id}` : undefined}
              disabled={
                busy ||
                !lifecycle ||
                republishNeeded ||
                !hasNamedTenantAction(productPolicy, `campaign.${lifecycle}`)
              }
              onClick={() => onAction(lifecycle)}
            >
              <CampaignIcon
                name={lifecycle === 'resume' ? 'resume' : 'pause'}
              />
            </Button>
          )}
          {lifecycleBlocker && <span className="bl-visually-hidden" id={`campaign-lifecycle-blocker-${id}`}>{lifecycleBlocker}</span>}
          {hasNamedTenantAction(
            productPolicy,
            archived ? 'campaign.restore' : 'campaign.archive',
          ) && (
            <Button
              variant="ghost"
              size="sm"
              type="button"
              className="bl-campaign-icon-button"
              style={ICON_BUTTON_STYLE}
              title={archived ? 'Restore campaign' : 'Archive campaign'}
              aria-label={`${archived ? 'Restore' : 'Archive'} campaign ${campaign.name}`}
              disabled={busy}
              onClick={() => onAction(archived ? 'restore' : 'archive')}
            >
              <CampaignIcon name={archived ? 'restore' : 'archive'} />
            </Button>
          )}
          {archived && hasNamedTenantAction(productPolicy, 'campaign.delete') && <Button
            variant="ghost" size="sm" type="button" className="bl-campaign-icon-button" style={ICON_BUTTON_STYLE}
            title="Delete campaign" aria-label={`Delete campaign ${campaign.name}`}
            disabled={busy} onClick={() => onAction('delete')}><CampaignIcon name="delete" /></Button>}
          <Button
            variant="ghost"
            size="sm"
            type="button"
            id={`campaign-details-${campaign.id}`}
            className="bl-campaign-details-button"
            style={DETAILS_BUTTON_STYLE}
            aria-label={`Details for ${campaign.name}`}
            onClick={onDetails}
          >
            Details
          </Button>
        </div>
  );
  return (
    <article
      className={`bl-campaign-summary-card bl-campaign-inventory-row is-${view}${expanded ? ' is-expanded' : ''}`}
      aria-labelledby={`campaign-title-${id}`}
    >
      <div className="bl-campaign-row-main">
        <div className="bl-campaign-summary-copy">
          <span className={`bl-campaign-type-symbol is-${typeSymbol}`}>
            <CampaignIcon name={typeSymbol} />
          </span>
          <div>
            <h3 id={`campaign-title-${id}`}>
              {campaign.name || 'Curated Meta campaign'}
            </h3>
            <p>
              {types.length
                ? types.join(' + ')
                : summaryStatus === 'loading'
                  ? 'Loading saved setup…'
                  : 'Saved campaign'}{' '}
              <span>
                · {budgetLabel}/mo

              </span>
            </p>
          </div>
        </div>
        <div className="bl-campaign-delivery-badges"><CampaignStatePill
          tone={campaignStatusTone(currentStatus)}
          className={`bl-campaign-state is-${currentStatus}`}
        >
          {currentStatus === 'awaiting_approval' ? 'In review' : currentStatus === 'failed' ? 'Check delivery' : statusLabel(currentStatus)}
        </CampaignStatePill>
        {republishNeeded && <CampaignStatePill tone="warn" className="bl-campaign-state is-republish-needed">Republish needed</CampaignStatePill>}
        {archived && <CampaignStatePill tone="neutral" className="bl-campaign-state is-archived">Archived</CampaignStatePill>}</div>
        <CampaignMetricStrip
          campaignName={campaign.name}
          datePreset={datePreset}
          summary={summary}
          summaryStatus={summaryStatus}
          compact
        />
        {view === 'list' && actions}
      </div>
      <div className="bl-campaign-row-bottom">
        <button
          type="button"
          className="bl-campaign-ads-disclosure"
          aria-expanded={expanded}
          aria-controls={`campaign-ads-${id}`}
          onClick={onToggle}
        >
          <CampaignIcon name="chevron" size={14} />
          {expanded ? 'Hide ads' : Number.isSafeInteger(summary?.settings?.selectedAdCount) ? `${summary.settings.selectedAdCount} ads` : 'View ads'}
        </button>
        {view === 'cards' && actions}
        <div className="bl-campaign-row-context"><span className="bl-campaign-row-note">{rowNote}</span></div>
      </div>
      {expanded && (
        <section
          id={`campaign-ads-${id}`}
          aria-label={`Ads for ${campaign.name}`}
        >
          <CampaignPerformanceContext datePreset={datePreset} summary={summary} summaryStatus={summaryStatus} />
          {children}
        </section>
      )}
    </article>
  );
}

export default function CampaignInventory({
  companyId,
  navigate,
  canCreate = true,
  productPolicy,
  initialFeedback = null,
}) {
  // OGC-452: a notice handed over by Details (e.g. "was deleted") shows once
  // on the list that replaces it, then follows the ordinary success timer.
  const handoffFeedback = useRef(initialFeedback);
  const billing = useCompanyBilling();
  const [billingPrompt, setBillingPrompt] = useState(null);
  useEffect(() => setBillingPrompt(null), [companyId, productPolicy]);
  const [preferences, setPreferences] = useState(() => ({
    companyId,
    ...inventoryPreferences(companyId),
  }));
  const prefs =
    preferences.companyId === companyId
      ? preferences
      : { companyId, ...inventoryPreferences(companyId) };
  const [searchInput, setSearchInput] = useState(prefs.search);
  const [pagination, setPagination] = useState(
    () =>
      prefs.pagination || {
        scope: '',
        index: 0,
        cursors: [''],
      },
  );
  const archived = prefs.status === 'archived' ? 'only' : 'exclude';
  const statusFilter = prefs.status === 'archived' ? '' : prefs.status;
  const pageMetricSort = ['leads', 'spend'].includes(prefs.sort);
  const serverSort = pageMetricSort ? 'recent' : prefs.sort;
  const baseScope = JSON.stringify([
    companyId,
    prefs.search,
    statusFilter,
    prefs.renovationTypeId,
    prefs.sort,
    archived,
  ]);
  const paging =
    pagination.scope === baseScope
      ? pagination
      : { scope: baseScope, index: 0, cursors: [''] };
  const listScope = JSON.stringify([
    baseScope,
    paging.index,
    paging.cursors[paging.index],
  ]);
  const [inventory, setInventory] = useState({
    companyId: '',
    scope: '',
    status: 'loading',
    page: emptyCampaignListPage(),
    types: [],
  });
  const [summaryState, setSummaryState] = useState({
    scope: '',
    status: 'loading',
    summaries: new Map(),
    batch: null,
  });
  const [expanded, setExpanded] = useState(() => new Set(prefs.expandedCampaignIds || []));
  const [panel, setPanel] = useState(null);
  const [budget, setBudget] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  useEffect(() => {
    if (feedback?.status !== 'success' || (panel && !feedback.toast)) return undefined;
    const timer = setTimeout(() => setFeedback(null), 5000);
    return () => clearTimeout(timer);
  }, [panel, feedback]);
  const confirmation = useConfirmationDialog();
  const searchRef = useRef(null);
  useEffect(() => {
    const shortcut = (event) => {
      if (event.key !== '/' || event.altKey || event.ctrlKey || event.metaKey
        || document.querySelector('dialog[open]')
        || /^(INPUT|SELECT|TEXTAREA)$/.test(event.target?.tagName) || event.target?.isContentEditable) return;
      event.preventDefault(); searchRef.current?.focus();
    };
    document.addEventListener('keydown', shortcut);
    return () => document.removeEventListener('keydown', shortcut);
  }, []);
  const context = useRef({});
  const mutationLock = useRef(false);
  const requestKeys = useRef(new Map());
  const trackedRequests = useRef(new Map());
  const listVersion = useRef(0);
  const summaryVersion = useRef(0);
  const panelVersion = useRef(0);
  const heading = useRef(null);
  const panelInvoker = useRef(null);
  const restorePanelHeading = useRef(false);
  const feedbackRef = useRef(null);
  const archiveFailureCancel = useRef(null);
  useEffect(() => {
    if (panel?.archiveFailure) archiveFailureCancel.current?.focus({ preventScroll: true });
  }, [panel?.archiveFailure]);
  useEffect(() => {
    if (!panel && restorePanelHeading.current) {
      restorePanelHeading.current = false;
      heading.current?.focus({ preventScroll: true });
    }
  }, [panel]);
  context.current = { companyId, listScope, panel, prefs, productPolicy, billing, budget };
  const updatePrefs = (patch) =>
    setPreferences((previous) => {
      const next = {
        ...(previous.companyId === companyId
          ? previous
          : { companyId, ...inventoryPreferences(companyId) }),
        ...patch,
      };
      remembered.set(companyId, next);
      if (patch.view) {
        try {
          localStorage.setItem('beyondleads-campaign-view', patch.view);
        } catch {
          /* Preference persistence is optional. */
        }
      }
      return next;
    });
  useEffect(() => {
    setPreferences({ companyId, ...inventoryPreferences(companyId) });
    setSearchInput(inventoryPreferences(companyId).search);
    setPanel(null);
    setExpanded(new Set(inventoryPreferences(companyId).expandedCampaignIds || []));
    const pending = handoffFeedback.current;
    handoffFeedback.current = null;
    setFeedback(pending?.message && typeof pending.message === 'string'
      ? { status: pending.status === 'error' ? 'error' : 'success', message: pending.message, toast: true }
      : null);
    panelVersion.current += 1;
  }, [companyId]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== prefs.search)
        updatePrefs({ search: searchInput.trim() });
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput, companyId]);
  useEffect(
    () => () => {
      context.current = {};
      listVersion.current += 1;
      summaryVersion.current += 1;
      panelVersion.current += 1;
    },
    [],
  );

  const loadList = useCallback(
    async ({ signal, preserve = false } = {}) => {
      const version = ++listVersion.current;
      setInventory((state) => ({
        companyId,
        scope: listScope,
        status: companyId ? 'loading' : 'empty',
        page:
          preserve && state.scope === listScope
            ? state.page
            : emptyCampaignListPage(paging.index),
        types: state.companyId === companyId ? state.types || [] : [],
        statusCounts: state.companyId === companyId ? state.statusCounts : null,
      }));
      if (!companyId) return;
      try {
        const response = await curatedCampaignManagementApi.list({
          companyId,
          archived,
          cursor: paging.cursors[paging.index],
          limit: CURATED_CAMPAIGN_LIST_PAGE_SIZE,
          search: prefs.search,
          renovationTypeId: prefs.renovationTypeId,
          status: statusFilter,
          sort: serverSort,
          signal,
        });
        if (
          version !== listVersion.current ||
          signal?.aborted ||
          context.current.listScope !== listScope
        )
          return;
        const page = normalizeCampaignListPageResponse(response, {
          pageIndex: paging.index,
        });
        const types = Array.isArray(response.filterOptions?.renovationTypes)
          ? response.filterOptions.renovationTypes
              .filter(
                (type) =>
                  typeof type?.id === 'string' &&
                  typeof type?.name === 'string',
              )
              .slice(0, 500)
          : [];
        setInventory({
          companyId,
          scope: listScope,
          status: 'ready',
          page,
          types,
          statusCounts: normalizeInventoryStatusCounts(response.statusCounts),
        });
      } catch (error) {
        if (
          version !== listVersion.current ||
          signal?.aborted ||
          context.current.listScope !== listScope
        )
          return;
        setInventory((state) => ({
          ...state,
          scope: listScope,
          status: 'error',
          error,
        }));
      }
    },
    [companyId, listScope],
  );
  useEffect(() => {
    const controller = new AbortController();
    loadList({ signal: controller.signal });
    return () => controller.abort();
  }, [loadList]);
  const current = inventory.scope === listScope;
  const page = current ? inventory.page : emptyCampaignListPage();
  const listStatus = current
    ? inventory.status
    : companyId
      ? 'loading'
      : 'empty';
  const campaigns = current ? page.items : [];
  const ids = useMemo(() => campaigns.map(({ id }) => id), [campaigns]);
  const summaryScope = campaignRowSummaryReadScopeKey(
    companyId,
    ids,
    prefs.datePreset,
  );
  context.current.summaryScope = summaryScope;
  const loadSummaries = useCallback(
    async ({ preserve = false, replace = false } = {}) => {
      const version = ++summaryVersion.current;
      if (!summaryScope) {
        setSummaryState({
          scope: summaryScope,
          status: 'ready',
          summaries: new Map(),
          batch: null,
        });
        return;
      }
      setSummaryState((state) => ({
        scope: summaryScope,
        status: 'loading',
        summaries:
          preserve && state.scope === summaryScope
            ? state.summaries
            : new Map(),
        batch: preserve && state.scope === summaryScope ? state.batch : null,
      }));
      try {
        const claim = campaignRowSummaryReadCoordinator.read(
          summaryScope,
          (signal) =>
            api.get(campaignRowSummaryPath(ids, prefs.datePreset), {
              ...headers(companyId),
              signal,
            }),
          { replace },
        );
        const response = await claim.promise;
        if (
          version !== summaryVersion.current ||
          context.current.summaryScope !== summaryScope
        )
          return;
        setSummaryState({
          scope: summaryScope,
          status: 'ready',
          summaries: normalizeCampaignRowSummaries(
            response,
            ids,
            prefs.datePreset,
          ),
          batch: normalizeCampaignRowSummaryBatch(response, prefs.datePreset),
        });
      } catch (error) {
        if (
          version !== summaryVersion.current ||
          context.current.summaryScope !== summaryScope
        )
          return;
        setSummaryState((state) => ({ ...state, status: 'error', error }));
      }
    },
    [companyId, summaryScope],
  );
  useEffect(() => {
    loadSummaries();
    return () => {
      if (summaryScope)
        campaignRowSummaryReadCoordinator.scheduleAbort(summaryScope);
    };
  }, [loadSummaries, summaryScope]);
  // OGC-478: rows waiting on a Meta resource refresh re-read on their own,
  // at most twice per summary scope while this view stays mounted; a scope
  // change or unmount cancels the pending re-read.
  const continuityRefetches = useRef(null);
  if (!continuityRefetches.current) continuityRefetches.current = createContinuityRefetchScheduler();
  const currentSummaryBatch =
    summaryState.scope === summaryScope ? summaryState.batch : null;
  useEffect(() => {
    if (!summaryScope || !summaryBatchIsRefreshing(currentSummaryBatch)) return undefined;
    continuityRefetches.current.request(
      summaryScope,
      CONTINUITY_SUMMARY_REFETCH_DELAY_MS,
      () => loadSummaries({ preserve: true, replace: true }),
    );
    return () => continuityRefetches.current.cancel();
  }, [currentSummaryBatch, loadSummaries, summaryScope]);
  const summaries =
    summaryState.scope === summaryScope ? summaryState.summaries : new Map();
  const summaryStatus =
    summaryState.scope === summaryScope ? summaryState.status : 'loading';
  const refresh = async () => {
    setRefreshNonce((value) => value + 1);
    await Promise.all([
      loadList({ preserve: true }),
      loadSummaries({ preserve: true, replace: true }),
    ]);
  };
  useEffect(() => {
    const invalidated = (event) => {
      if (event.detail?.companyId !== companyId || mutationLock.current) return;
      loadList({ preserve: true });
      loadSummaries({ preserve: true, replace: true });
    };
    window.addEventListener(LIST_INVALIDATED_EVENT, invalidated);
    return () => window.removeEventListener(LIST_INVALIDATED_EVENT, invalidated);
  }, [companyId, loadList, loadSummaries]);
  const openDetails = (campaign) => {
    remembered.set(companyId, {
      ...prefs,
      pagination: paging,
      returnCampaignId: campaign.id,
      expandedCampaignIds: [...expanded],
    });
    navigate?.(campaignFullDetailDestination(campaign.id));
  };
  useEffect(() => {
    if (listStatus !== 'ready' || !prefs.returnCampaignId) return;
    (document.getElementById(`campaign-details-${prefs.returnCampaignId}`) || heading.current)
      ?.focus({ preventScroll: true });
    updatePrefs({ returnCampaignId: '' });
  }, [listStatus]);

  async function openAction(campaign, mode) {
    if (mutationLock.current) return;
    // OGC-557: a known payment block needs no management/provider detail read.
    // The row's role and known provider restrictions still run first.
    if (mode === 'resume' && hasNamedTenantAction(productPolicy, 'campaign.resume')
      && !campaignSpendAccess(billing, companyId).allowed) {
      setBillingPrompt({ companyId, action: 'resume' });
      return;
    }
    if (!panel) panelInvoker.current = document.activeElement;
    const version = ++panelVersion.current;
    setFeedback(null);
    setBudget(
      summaries.get(campaign.id)?.settings?.monthlyBudget?.monthlyMajor || '',
    );
    setPanel({
      campaign,
      mode,
      status: 'loading',
      detail: null,
      management: null,
    });
    try {
      const managementResponse = await curatedCampaignManagementApi.get({
        companyId,
        campaignId: campaign.id,
      });
      const management = normalizeCampaignManagementResponse(
        managementResponse,
        campaign.id,
      );
      let detail = null;
      if (
        mode === 'delete' || (!management.archivedAt &&
        ['settings', 'pause', 'resume'].includes(mode))
      )
        detail = validatedCampaignDetail(
          await api.get(
            detailPath(campaign.id, prefs.datePreset),
            headers(companyId),
          ),
          campaign.id,
        );
      if (
        version !== panelVersion.current ||
        context.current.companyId !== companyId
      )
        return;
      const hydrated = detail
        ? hydrateRetryStateFromDetail(
            requestKeys.current,
            trackedRequests.current,
            { companyId, campaignId: campaign.id, detail },
          )
        : null;
      if (mode === 'settings' && hydrated?.tracked?.action === 'budget')
        setBudget(hydrated.tracked.monthlyMajor);
      setPanel({ campaign, mode, status: 'ready', management, detail });
    } catch (error) {
      if (
        version !== panelVersion.current ||
        context.current.companyId !== companyId
      )
        return;
      setPanel({
        campaign,
        mode,
        status: 'error',
        error,
        detail: null,
        management: null,
      });
    }
  }

  async function mutate(action) {
    const selected = context.current.panel;
    if (!selected || selected.status !== 'ready' || mutationLock.current)
      return;
    const campaignId = selected.campaign.id;
    const expectedPanelVersion = panelVersion.current;
    const managementAction = ['archive', 'restore', 'delete'].includes(action);
    const controls = campaignControlsForPolicy(selected.detail, productPolicy);
    const allowed = managementAction
      ? selected.management?.actions?.[action]?.allowed &&
        hasNamedTenantAction(productPolicy, `campaign.${action}`)
      : !selected.management?.archivedAt && controls[action];
    if (!allowed || (action === 'delete' && campaignDeleteRetryWaitMinutes(selected.management?.deletion))) return;
    if (action === 'resume' && !campaignSpendAccess(context.current.billing, companyId).allowed) {
      setBillingPrompt({ companyId, action });
      return;
    }
    const amount = action === 'budget' ? canonicalMonthlyMajor(budget) : '';
    if (action === 'budget' && !amount) {
      setFeedback({
        status: 'error',
        message:
          'Enter a positive monthly budget with no more than two decimal places.',
      });
      return;
    }
    const currency = campaignBudgetMutationCurrency(selected.detail);
    if (action === 'budget' && !/^[A-Z]{3}$/.test(currency)) {
      setFeedback({
        status: 'error',
        message:
          'The account currency could not be verified. Refresh campaign controls before changing the budget.',
      });
      return;
    }
    const budgetAccess = action === 'budget' ? campaignBudgetAccess({ billing: context.current.billing, companyId, detail: selected.detail, monthlyMajor: amount }) : null;
    if (budgetAccess && !budgetAccess.allowed) {
      setBillingPrompt({ companyId, action: 'budget' });
      return;
    }
    if (action === 'budget' && budgetAccess.unchanged) {
      setFeedback({ status: 'success', message: 'The budget is unchanged.', toast: true });
      return;
    }
    if (action === 'budget') {
      const baseline = campaignBudgetBaseline(selected.detail);
      const approved = await confirmation.request({
        title: 'Update campaign budget?',
        description: `Set ${selected.campaign.name} to ${amount} ${currency} per month (about ${(Number(amount) / 30.4).toFixed(2)} ${currency}/day).${baseline ? ` Meta’s current verified daily budget is ${(baseline.dailyMinor / (10 ** baseline.minorUnitExponent)).toFixed(baseline.minorUnitExponent)} ${baseline.currency}.` : ''} Delivery keeps its current status. This is not a hard monthly spending cap.`,
        confirmLabel: 'Update budget',
        tone: 'warning',
      });
      if (
        !approved ||
        panelVersion.current !== expectedPanelVersion ||
        context.current.companyId !== companyId ||
        context.current.panel !== selected ||
        canonicalMonthlyMajor(context.current.budget) !== amount ||
        !campaignControlsForPolicy(context.current.panel?.detail, context.current.productPolicy).budget ||
        campaignBudgetMutationCurrency(context.current.panel?.detail) !== currency ||
        !campaignBudgetAccess({ billing: context.current.billing, companyId, detail: context.current.panel?.detail, monthlyMajor: amount }).allowed ||
        mutationLock.current
      )
        return;
      if (campaignBudgetAccess({ billing: context.current.billing, companyId, detail: context.current.panel?.detail, monthlyMajor: amount }).unchanged) {
        setFeedback({ status: 'success', message: 'The budget is unchanged.', toast: true });
        return;
      }
    }
    let typedConfirmationName;
    if (action === 'delete') {
      if (!selected.detail?.campaign?.name?.trim()) return;
      const approved = await confirmation.request(campaignDeleteDialogOptions(selected.detail, selected.management));
      const latest = context.current.panel;
      if (!approved || panelVersion.current !== expectedPanelVersion || context.current.companyId !== companyId
        || mutationLock.current || latest !== selected
        || !latest.management?.actions?.delete?.allowed
        || campaignDeleteRetryWaitMinutes(latest.management?.deletion)) return;
      typedConfirmationName = typeof approved.typedValue === 'string' ? approved.typedValue.trim() : '';
    }
    mutationLock.current = true;
    setPanel((state) => ({ ...state, busy: true }));
    setFeedback({
      status: 'pending',
      message:
        action === 'archive'
          ? (selected.management?.archiveWarning?.reasonCode === 'campaign_archive_draft_only' ? 'Archiving the draft in BeyondLeads. No Meta delivery changes are needed.' : 'Checking delivery, pausing if needed, then archiving. The campaign stays visible until Meta confirms it is paused.')
          : action === 'delete' ? 'Confirming campaign deletion…' : 'Waiting for provider confirmation…',
    });
    const keyIdentity = `${companyId}:${campaignId}:${action}`;
    const retry = managementAction
      ? {
          cacheIdentity: keyIdentity,
          idempotencyKey:
            requestKeys.current.get(keyIdentity) ||
            campaignManagementRetryKey(action, companyId, campaignId),
        }
      : scopedOperationRetryKey(requestKeys.current, {
          companyId,
          campaignId,
          action,
          monthlyMajor: amount,
        });
    if (managementAction)
      requestKeys.current.set(keyIdentity, retry.idempotencyKey);
    const stillCurrent = () =>
      context.current.companyId === companyId &&
      panelVersion.current === expectedPanelVersion;
    let refreshProviderProjection = true;
    try {
      if (managementAction) {
        const response = await curatedCampaignManagementApi[action]({
          companyId,
          campaignId,
          expectedRevision: selected.management.configurationRevision,
          idempotencyKey: retry.idempotencyKey,
          ...(action === 'delete' ? { confirmed: true, confirmationName: typedConfirmationName } : {}),
        });
        if (action === 'delete') {
          normalizeCampaignDeletionResponse(response, campaignId);
          invalidateCampaignManagementList(companyId);
          requestKeys.current.delete(retry.cacheIdentity);
          if (!stillCurrent()) return;
          setPanel(null);
          panelVersion.current += 1;
          setFeedback({ status: 'success', message: `“${selected.campaign.name}” was deleted.` });
          return;
        }
        const management = normalizeCampaignManagementResponse(
          response,
          campaignId,
        );
        const confirmed =
          action === 'archive'
            ? Boolean(management.archivedAt)
            : management.archivedAt === null;
        if (!confirmed)
          throw new Error('Campaign management confirmation is unavailable.');
        invalidateCampaignManagementList(companyId);
        requestKeys.current.delete(retry.cacheIdentity);
        if (!stillCurrent()) return;
        setPanel(null);
        panelVersion.current += 1;
        setFeedback({
          status: 'success',
          message:
            action === 'archive'
              ? selected.management.archiveWarning.reasonCode ===
                'campaign_archive_draft_only'
                ? 'Draft archived. Its saved setup and history are preserved.'
                : 'Campaign delivery is confirmed paused and the campaign is archived. History and individual ad settings are preserved.'
              : 'Campaign restored. Delivery has not resumed. Open Details to verify its current state before resuming.',
        });
      } else {
        const response = await api.post(
          `/api/saas/campaigns/${encodeURIComponent(campaignId)}/operations/${action}`,
          mutationBody(action, retry.idempotencyKey, amount),
          headers(companyId),
        );
        const detail = validatedCampaignDetail(
          await api.get(
            detailPath(campaignId, prefs.datePreset),
            headers(companyId),
          ),
          campaignId,
        );
        if (!stillCurrent()) return;
        const operationId = mutationOperationId(response);
        const presentation = operationId
          ? durableMutationPresentation(
              durableMutationReconciliation(detail, action, operationId),
              action === 'budget'
                ? 'Budget update'
                : `${action === 'pause' ? 'Pause' : 'Resume'} campaign`,
            )
          : {
              status: 'warning',
              message:
                'The request was accepted, but its exact operation status is unavailable. Refresh before retrying; the same request will be reused.',
            };
        if (presentation.clearRetryKey)
          requestKeys.current.delete(retry.cacheIdentity);
        if (presentation.status === 'success') {
          setPanel(null);
          panelVersion.current += 1;
        } else setPanel((state) => ({ ...state, detail, busy: false }));
        setFeedback(presentation);
      }
    } catch (error) {
      if (!stillCurrent()) return;
      if (['billing_subscription_required', 'campaign_budget_billing_evidence_required'].includes(error?.code)) {
        refreshProviderProjection = false;
        if (error.code === 'billing_subscription_required') {
          await context.current.billing.refresh();
          if (!stillCurrent()) return;
          setBillingPrompt({ companyId, action, serverDenied: true });
        }
      }
      if (managementAction && !retainInventoryManagementRetry(error)) {
        requestKeys.current.delete(retry.cacheIdentity);
      }
      let refreshedManagement = null;
      if (action === 'delete') {
        try {
          refreshedManagement = normalizeCampaignManagementResponse(
            await curatedCampaignManagementApi.get({ companyId, campaignId }), campaignId,
          );
        } catch { /* Keep the action locked until a fresh explicit controls read succeeds. */ }
        if (!stillCurrent()) return;
      }
      setPanel((state) => ({ ...state, busy: false,
        ...(refreshedManagement ? { management: refreshedManagement } : {}),
        // OGC-455: this row panel never offers the local-only archive — one
        // confirmation implementation for it is enough, and it lives in
        // Details. It still has to name the cause rather than shrug.
        ...(action === 'delete' ? { deleteFailure: true } : {}),
        ...(action === 'archive' ? { archiveFailure: error?.code === 'campaign_archive_pause_unconfirmed' ? 'pause_unconfirmed' : error?.code === 'campaign_archive_pause_connection_required' ? 'pause_connection_required' : 'unconfirmed_outcome' } : {}),
      }));
      setFeedback({
        status: 'error',
        message: safeFailureMessage(error, 'curated-campaign'),
      });
    } finally {
      mutationLock.current = false;
      if (context.current.companyId === companyId) {
        if (refreshProviderProjection) await refresh();
        if (managementAction && !context.current.panel)
          (feedbackRef.current || heading.current)?.focus({
            preventScroll: true,
          });
      }
    }
  }

  const statusCounts = inventoryStatusCountsForCompany(inventory, companyId);
  const displayedCampaigns = sortInventoryPage(campaigns, summaries, prefs.sort);
  const verifiedPeriods = [...new Set([...summaries.values()].map((summary) => campaignReportingPeriod(summary, prefs.datePreset)))];
  const reportingCaption = verifiedPeriods.length === 1 ? verifiedPeriods[0] : '';
  const selectedSummary = panel ? summaries.get(panel.campaign.id) : null;
  const selectedControls = campaignControlsForPolicy(
    panel?.detail,
    productPolicy,
  );
  const spendAccess = campaignSpendAccess(billing, companyId);
  const resumeBillingBlock = panel?.mode === 'resume' && panel.status === 'ready' && selectedControls.resume && !spendAccess.allowed;
  const selectedBudgetAccess = panel?.mode === 'settings' && selectedControls.budget
    ? campaignBudgetAccess({ billing, companyId, detail: panel.detail, monthlyMajor: canonicalMonthlyMajor(budget) || '' }) : null;
  const deleteRetryWait = useCampaignDeleteRetryWait(panel?.management?.deletion);
  const selectedAllowed = panel?.status === 'ready' && !panel.deleteFailure
    && !(panel.mode === 'delete' && deleteRetryWait)
    && (['archive', 'restore', 'delete'].includes(panel.mode)
      ? panel.management?.actions?.[panel.mode]?.allowed && hasNamedTenantAction(productPolicy, `campaign.${panel.mode}`)
      : selectedControls[panel.mode] && !(panel.mode === 'resume' && !spendAccess.allowed));
  const archiveFailure = panel?.mode === 'archive' && panel.archiveFailure;
  const archiveIsDraftOnly = panel?.management?.archiveWarning?.reasonCode === 'campaign_archive_draft_only';
  const panelTitle = archiveFailure
    ? ['pause_unconfirmed', 'pause_connection_required'].includes(archiveFailure) ? 'Campaign wasn’t archived.' : 'Archive needs attention'
    :
    panel?.mode === 'delete' ? 'Delete campaign' : panel?.mode === 'settings'
      ? 'Ad settings'
      : panel?.mode === 'archive'
        ? ['active', 'partial', 'failed'].includes(panel.campaign.status) ? 'Pause & archive this campaign?' : 'Archive this campaign?'
        : panel?.mode === 'restore'
          ? 'Restore this campaign?'
          : `${panel?.mode === 'pause' ? 'Pause' : 'Resume'} this campaign?`;
  const panelDescription = archiveFailure
    ? archiveFailure === 'pause_unconfirmed'
      ? 'Delivery could not be confirmed paused. The campaign remains visible.'
      : archiveFailure === 'pause_connection_required'
        ? 'Meta can’t show us this campaign, so it can’t be confirmed paused. The campaign remains visible. Open it to see your options.'
        : 'The archive outcome could not be confirmed. Review the current campaign state before trying again.'
    : panel?.mode === 'delete' ? 'Review the current deletion eligibility before confirming.' : panel?.mode === 'archive'
    ? 'Published campaigns are paused and verified with Meta before moving to Archived. If verification fails, the campaign stays visible. Drafts archive directly. Your history, leads, and individual ad settings stay saved.'
    : panel?.mode === 'restore'
      ? 'The campaign returns to your current inventory. Delivery will not resume. Published campaigns require current provider verification before you explicitly resume.'
      : panel?.mode === 'pause'
        ? 'Delivery stops at the campaign level. Budget, performance history, and individual ad settings are preserved.'
        : panel?.mode === 'resume'
          ? 'Resume uses the currently enabled ads and preserves ads you paused individually. Meta review and parent status still determine delivery. If no ads are enabled, the campaign cannot deliver until an ad is enabled.'
          : '';
  const closePanel = () => {
    if (!mutationLock.current) {
      restorePanelHeading.current = Boolean(panelInvoker.current && !panelInvoker.current.isConnected);
      panelVersion.current += 1;
      setPanel(null);
      setFeedback(null);
    }
  };
  const Panel = panel?.mode === 'settings' ? CampaignSettingsDrawer : CampaignModal;
  return (
    <section
      className="bl-campaigns-page bl-campaign-inventory"
      aria-labelledby="curated-campaigns-title"
    >
      <header className="bl-campaigns-heading">
        <div>
          <div className="bl-kicker">Curated Meta Ads</div>
          <h1 id="curated-campaigns-title">Campaigns</h1>
          <p>
            A clear view of what’s running. Simple controls to keep it moving.
          </p>
        </div>
        {canCreate && (
          <Button variant="accent" size="sm" type="button" onClick={() => navigate?.('curated-campaign')}>
            ＋ New campaign
          </Button>
        )}
      </header>
      <div className="bl-campaign-reporting-toolbar">
        <span className="bl-campaign-insights-source"><i aria-hidden="true" />Meta Insights <span aria-hidden="true">/</span> Campaign performance</span>
        <label>
          <span className="bl-visually-hidden">Reporting period</span>
          {reportingCaption && <span aria-hidden="true" className="bl-campaign-period-caption">{reportingCaption}</span>}
          <Select
            aria-label="Reporting period"
            value={prefs.datePreset}
            onChange={(datePreset) => updatePrefs({ datePreset })}
            options={PERIODS.map(([value, label]) => ({ value, label }))}
          />
        </label>
      </div>
      <CampaignInventoryOverview campaigns={campaigns} summaries={summaries} summaryStatus={summaryStatus} listStatus={listStatus} />
      <section
        className="bl-resumable-campaigns"
        aria-labelledby="saved-campaigns-title"
      >
        <div
          className="bl-campaign-status-tabs"
          role="group"
          aria-label="Campaign status filter"
        >
          {STATUS_FILTERS.map(([value, label]) => (
            <Chip
              key={value}
              aria-label={label}
              pressed={prefs.status === value}
              onClick={() => updatePrefs({ status: value })}
            >
              {label}<span className="bl-chip-count" aria-hidden="true" title={statusCounts ? 'Company campaigns, independent of search and page' : 'Counts unavailable'}>{statusCounts?.[value || 'all'] ?? '—'}</span>
            </Chip>
          ))}
        </div>
        <div className="bl-campaign-inventory-toolbar">
          <label className="bl-campaign-search">
            <CampaignIcon name="search" />
            <span className="bl-visually-hidden">Search campaigns or ads</span>
            <input
              type="search"
              ref={searchRef}
              placeholder="Search campaigns or ads…"
              maxLength={120}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>
          <label className="bl-campaign-type-filter">
            <CampaignIcon name="settings" size={14} /><span className="bl-visually-hidden">Renovation type</span>
            <Select
              aria-label="Renovation type"
              value={prefs.renovationTypeId}
              onChange={(renovationTypeId) => updatePrefs({ renovationTypeId })}
              options={[
                { value: '', label: 'All renovation types' },
                ...(inventory.companyId === companyId ? inventory.types : []).map(
                  (type) => ({ value: type.id, label: type.name }),
                ),
              ]}
            />
          </label>
          <label>
            <span className="bl-visually-hidden">Sort campaigns</span>
            <Select
              aria-label="Sort campaigns"
              value={prefs.sort}
              aria-describedby="campaign-sort-scope"
              onChange={(sort) => updatePrefs({ sort })}
              options={[
                { value: 'recent', label: 'Newest campaigns' },
                { value: 'leads', label: 'Most leads' },
                { value: 'spend', label: 'Highest spend' },
                { value: 'name_asc', label: 'Name A–Z' },
                { value: 'name_desc', label: 'Name Z–A' },
              ]}
            />
          </label>
          <div
            className="bl-campaign-view-toggle"
            role="group"
            aria-label="Campaign view"
          >
            {['list', 'cards'].map((view) => (
              <button
                key={view}
                type="button"
                aria-label={`${view === 'list' ? 'List' : 'Card'} view`}
                aria-pressed={prefs.view === view}
                onClick={() => updatePrefs({ view })}
              >
                <CampaignIcon name={view} />
              </button>
            ))}
          </div>
        </div>
        <div className="bl-resumable-heading">
          <h2 className="bl-visually-hidden" id="saved-campaigns-title" ref={heading} tabIndex={-1}>
            Saved campaigns
          </h2>
          <span className="bl-campaign-result-range">
            {listStatus === 'ready' ? page.rangeLabel : 'Campaign inventory'}
          </span>
          {(prefs.search || prefs.status || prefs.renovationTypeId) && <button className="bl-campaign-clear-filters" type="button" onClick={() => {
            setSearchInput(''); updatePrefs({ search: '', status: '', renovationTypeId: '' });
          }}>Clear filters</button>}
          <span id="campaign-sort-scope" className={`bl-campaign-results-hint${pageMetricSort ? ' is-page-metric-sort' : ''}`}>{prefs.sort === 'spend' && !inventorySpendSortCurrency(campaigns, summaries) ? 'Spend ordering needs a single reporting currency · ' : pageMetricSort ? 'Performance sorted within this page · ' : ''}Leads are Meta-reported conversions</span>
        </div>
        {feedback?.message && !panel && (
          <div
            ref={feedbackRef}
            tabIndex={-1}
            className={`bl-dismissible-notice bl-campaign-toast is-${feedback.status}`}
            role={feedback.status === 'error' ? 'alert' : 'status'}
          >
            {feedback.message}
          </div>
        )}
        {listStatus === 'loading' && <p><Beacon size="control" label="Loading campaigns…" delayMs={0} slowAfterMs={null} /></p>}
        {listStatus === 'empty' && (
          <p>Choose a company to view its campaigns.</p>
        )}
        {listStatus === 'error' && (
          <div role="alert">
            <p>{safeFailureMessage(inventory.error, 'curated-campaign')}</p>
            <button type="button" onClick={() => loadList({ preserve: true })}>
              Retry campaign page
            </button>
          </div>
        )}
        {listStatus === 'ready' && !campaigns.length && (
          <div className="bl-campaign-empty">
            <CampaignIcon name="search" size={26} />
            <h3>No campaigns found</h3>
            <p>
              {prefs.search || prefs.status || prefs.renovationTypeId
                ? 'Try another search or clear your filters.'
                : 'Your saved campaigns will appear here.'}
            </p>
            {(prefs.search || prefs.status || prefs.renovationTypeId) && (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => {
                  setSearchInput('');
                  updatePrefs({ search: '', status: '', renovationTypeId: '' });
                }}
              >
                Clear filters
              </Button>
            )}
          </div>
        )}
        {campaigns.length > 0 && (
          <>
            <CampaignSummaryBatchStatus
              batch={
                summaryState.scope === summaryScope ? summaryState.batch : null
              }
              hasPreservedData={summaries.size > 0}
              onRetry={() => loadSummaries({ preserve: true, replace: true })}
              rangeLabel={page.summaryRangeLabel}
              refreshExhausted={Boolean(summaryScope)
                && continuityRefetches.current.exhausted(summaryScope)}
              status={summaryStatus}
            />
            {prefs.view === 'list' && <div className="bl-campaign-list-header" aria-hidden="true">
              <span>Campaign</span><span>Status</span>
              <div className="bl-campaign-list-metric-headings"><span>Leads</span><span>Cost / lead</span><span>Spend</span><span>CTR</span></div>
              <span>Quick actions</span>
            </div>}
            <div
              className={`bl-campaign-list bl-campaign-inventory-grid is-${prefs.view}`}
            >
              {displayedCampaigns.map((campaign) => (
                <CampaignInventoryRow
                  key={campaign.id}
                  campaign={campaign}
                  summary={summaries.get(campaign.id)}
                  summaryStatus={summaryStatus}
                  view={prefs.view}
                  datePreset={prefs.datePreset}
                  expanded={expanded.has(campaign.id)}
                  busy={panel?.busy}
                  productPolicy={productPolicy}
                  onToggle={() =>
                    setExpanded((previous) => {
                      const next = new Set(previous);
                      if (next.has(campaign.id)) next.delete(campaign.id); else next.add(campaign.id);
                      return next;
                    })
                  }
                  onAction={(action) => openAction(campaign, action)}
                  onDetails={() => openDetails(campaign)}
                >
                  <CampaignAds
                    companyId={companyId}
                    campaign={campaign}
                    datePreset={prefs.datePreset}
                    productPolicy={productPolicy}
                    refreshNonce={refreshNonce}
                    onChanged={refresh}
                  />
                </CampaignInventoryRow>
              ))}
            </div>
          </>
        )}
        <CampaignListPagination
          page={page}
          onPageChange={(index) => {
            const cursors = [...paging.cursors];
            if (index > paging.index) cursors[index] = page.nextCursor;
            setPagination({ scope: baseScope, index, cursors });
            heading.current?.focus();
          }}
        />
      </section>
      {panel && (
        <Panel
          title={panel.busy && panel.mode === 'archive' ? (archiveIsDraftOnly ? 'Archiving campaign…' : 'Pausing & archiving…') : panelTitle}
          description={panel.mode === 'settings' ? panel.campaign.name : `${panel.campaign.name}. ${panelDescription}`}
          icon={<CampaignIcon name={panel.mode} size={20} />}
          busy={panel.busy}
          confirmationOpen={confirmation.dialogProps.open}
          onClose={closePanel}
          footer={panel.mode === 'settings' ? <>
            <button type="button" disabled={panel.busy} onClick={() => openDetails(panel.campaign)}>Open details</button>
            {selectedControls.budget && selectedSummary?.settings?.monthlyBudget?.canUpdate && !panel.management?.archivedAt &&
              <button type="submit" form="campaign-quick-settings" className="is-yellow" disabled={panel.busy || selectedBudgetAccess?.allowed === false}>
                {panel.busy ? 'Updating…' : 'Review budget change'}
              </button>}
          </> : <>
            <button ref={archiveFailureCancel} type="button" data-campaign-safe-focus disabled={panel.busy} onClick={closePanel}>Cancel</button>
            {archiveFailure ? <button type="button" className="is-primary" onClick={() => openDetails(panel.campaign)}>View details</button>
              : resumeBillingBlock ? <button type="button" className="is-primary" onClick={() => spendAccess.reason === 'subscription_required' ? navigate?.('billing') : billing.refresh()}>{spendAccess.reason === 'subscription_required' ? 'View Billing' : 'Check status'}</button>
                : panel.status === 'ready' && !selectedAllowed && <button type="button" onClick={() => openDetails(panel.campaign)}>Open details</button>}
            {!archiveFailure && <button type="button" className="is-primary" disabled={!selectedAllowed || panel.busy}
              onClick={() => mutate(panel.mode)}>
              {panel.busy ? 'Please wait…' : panel.mode === 'archive' ? 'Archive campaign'
                : panel.mode === 'restore' ? 'Restore campaign' : panel.mode === 'delete' ? 'Review deletion' : `${panel.mode === 'pause' ? 'Pause' : 'Resume'} campaign`}
            </button>}
          </>}

        >
          <Beacon size="control" active={Boolean(panel.busy || (panel.mode === 'delete' && deleteRetryWait))} label={panel.mode === 'delete' && !panel.busy && deleteRetryWait ? `Delete retry available in about ${deleteRetryWait} min.` : panel.mode === 'delete' ? 'Confirming campaign deletion…' : panel.mode === 'archive' ? 'Confirming archive…' : 'Confirming campaign update…'}
            slowAfterMs={!panel.busy || (panel.mode === 'delete' && panel.management?.deleteEligibility?.mode === 'hard') || (panel.mode === 'archive' && panel.management?.archiveWarning?.reasonCode === 'campaign_archive_draft_only') ? null : 8000} />
          {panel.mode === 'delete' && !panel.busy && panel.status === 'ready' && <p role="note">{deleteRetryWait ? `Try deleting again in about ${deleteRetryWait} min.` : !panel.management?.actions?.delete?.allowed ? campaignDeleteBlockerMessage(panel.management) : ''}</p>}
          {feedback?.message && !archiveFailure && (
            <div
              className={`bl-dismissible-notice bl-campaign-toast bl-campaign-panel-notice is-${feedback.status}`}
              role={feedback.status === 'error' ? 'alert' : 'status'}
            >
              <span className="bl-dismissible-notice-content">{feedback.message}</span>
              {['error', 'warning'].includes(feedback.status) &&
                !panel.busy && !archiveFailure && (
                  <button
                    type="button"
                    onClick={() => openAction(panel.campaign, panel.mode)}
                  >
                    Refresh campaign controls
                  </button>
                )}
            </div>
          )}
          {panel.mode === 'archive' && <div className="bl-campaign-action-notice" role={archiveFailure ? 'alert' : undefined}><strong>{archiveFailure ? 'Review before retrying' : archiveIsDraftOnly ? 'Archive draft' : 'Pause → confirm → archive'}</strong>{archiveFailure
            ? feedback?.message || 'Open campaign details to review delivery and recovery. Archiving again requires a fresh review of current controls.'
            : archiveIsDraftOnly ? 'The draft moves to Archived in BeyondLeads. No Meta delivery changes are needed.' : 'If we can’t confirm delivery is paused, the campaign stays visible. Restoring it later will not resume delivery.'}</div>}
          {panel.mode === 'resume' && <div className="bl-campaign-action-notice">Ads you paused individually remain paused. Enabled ads share the campaign budget.</div>}
          {resumeBillingBlock && <CampaignBillingAccessNotice decision={spendAccess} />}
          {panel.status === 'loading' && (
            <p><Beacon size="control" label="Checking current campaign controls…" delayMs={0} slowAfterMs={null} /></p>
          )}
          {panel.status === 'error' && (
            <div role="alert">
              <p>{safeFailureMessage(panel.error, 'curated-campaign')}</p>
              <button
                type="button"
                onClick={() => openAction(panel.campaign, panel.mode)}
              >
                Retry campaign controls
              </button>
            </div>
          )}
          {panel.status === 'ready' && panel.mode === 'settings' && (
            <>
              <CampaignSettings
                campaign={panel.campaign}
                summary={
                  selectedSummary
                    ? {
                        ...selectedSummary,
                        settings: {
                          ...selectedSummary.settings,
                          monthlyBudget: {
                            ...selectedSummary.settings.monthlyBudget,
                            canUpdate:
                              selectedSummary.settings.monthlyBudget
                                .canUpdate &&
                              selectedControls.budget &&
                              !panel.management.archivedAt,
                          },
                        },
                      }
                    : null
                }
                productPolicy={productPolicy}
                formId="campaign-quick-settings"
                showSubmit={false}
                budgetValue={budget}
                budgetInputId="campaign-quick-budget"
                budgetBusy={panel.busy}
                budgetAccess={selectedBudgetAccess}
                onViewBilling={() => navigate?.('billing')}
                onRefreshBudget={() => openAction(panel.campaign, 'settings')}
                onBudgetChange={setBudget}
                onBudgetSubmit={() => mutate('budget')}
              />

            </>
          )}
          {panel.status === 'ready' && panel.mode !== 'settings' && (
            <>
              {!selectedAllowed && !resumeBillingBlock && (
                <p role="note">
                  This action is unavailable for the current role or verified
                  campaign state. Open Details to review setup, provider status,
                  and recovery options.
                </p>
              )}
            </>
          )}
        </Panel>
      )}
      {billingPrompt?.companyId === companyId && hasNamedTenantAction(productPolicy, billingPrompt.action === 'budget' ? 'campaign.budget.update' : 'campaign.resume') && <CampaignBillingAccessDialog
        serverDenied={billingPrompt.serverDenied}
        decision={billingPrompt.action === 'budget' ? campaignBudgetAccess({ billing, companyId, detail: panel?.detail, monthlyMajor: canonicalMonthlyMajor(budget) || '' }) : campaignSpendAccess(billing, companyId)}
        onClose={() => setBillingPrompt(null)} onViewBilling={() => { setBillingPrompt(null); navigate?.('billing'); }}
        onRefresh={async () => { await billing.refresh(); setBillingPrompt(null); }} />}
      <CampaignConfirmationDialog {...confirmation.dialogProps} icon={<CampaignIcon name={panel?.mode === "delete" ? "delete" : "settings"} size={20} />}>{panel?.mode !== "delete" && <div className="bl-campaign-action-notice">The campaign keeps its current delivery status. All enabled ads share this budget.</div>}</CampaignConfirmationDialog>
    </section>
  );
}
